<script setup lang="ts">
import { computed, ref } from 'vue';
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import { medicationsApi, type MedicationOrder, type OrderListStatus } from '@/api/medications';
import { usePermissions } from '@/composables/usePermissions';
import { useResponsiveTable } from '@/composables/useResponsiveTable';
import { apiErrorText } from '@/utils/api-errors';
import { allergiesTo, allergyStatus } from '@/utils/allergies';
import ClinicalRecordHistory from '@/components/clinical-records/clinical-record-history.vue';
import ClinicalAuthorLine from '@/components/clinical/clinical-author-line.vue';
import MedicationOrderForm from './medication-order-form.vue';
import MedicationOrderActionDialog, { type OrderAction } from './medication-order-action-dialog.vue';
import MedicationDayRecord from './medication-day-record.vue';
import MedicationAllergiesDialog from './medication-allergies-dialog.vue';
import MedicationAllergiesPanel from './medication-allergies-panel.vue';

// A patient's medication plan: their allergies, orders in force, upcoming ones
// (a change "desde mañana") and the history. Orders are never edited: they are
// changed (a new order replaces them), stopped, paused or voided. `readOnly`
// (the company turned the module off) leaves the history to read, with no actions.
const props = defineProps<{ patientId: number; archived?: boolean; readOnly?: boolean }>();

const { hasPermission } = usePermissions();
const queryClient = useQueryClient();
const canManage = computed(() => !props.readOnly && hasPermission.value('medications', 'manage'));
const { tableProps } = useResponsiveTable();

const status = ref<OrderListStatus | 'record' | 'allergies'>('in_force');
const showForm = ref(false);
const replacing = ref<MedicationOrder | null>(null);
const acting = ref<{ order: MedicationOrder; action: OrderAction } | null>(null);
const editingAllergies = ref(false);
const error = ref('');

const { data: allergies, isPending: allergiesPending } = useQuery({
  queryKey: ['patientAllergies', computed(() => props.patientId)],
  queryFn: () => medicationsApi.allergies(props.patientId),
});
// Nobody recorded them yet: the button says "Registrar".
const allergiesUnrecorded = computed(() => allergyStatus(allergies.value ?? []) === 'unknown');

// An order whose drug the patient is allergic to (the allergy may have come later).
function allergic(order: MedicationOrder): boolean {
  return allergiesTo(allergies.value ?? [], order.substanceIds).length > 0;
}

const { data: orders, isPending } = useQuery({
  queryKey: ['medicationOrders', computed(() => props.patientId), status],
  queryFn: () => medicationsApi.orders(props.patientId, status.value as OrderListStatus),
  enabled: computed(() => status.value !== 'record' && status.value !== 'allergies'),
  initialData: [],
});

const headers = computed(() => [
  { title: 'Medicamento', key: 'drugName' },
  { title: 'Dosis', key: 'dosages', sortable: false },
  { title: 'Vía', key: 'routeLabel' },
  { title: 'Lo provee', key: 'humanSuppliedBy' },
  { title: 'Período', key: 'startsAt' },
  ...(canManage.value && status.value !== 'history' ?
    [{ title: '', key: 'actions', sortable: false, align: 'end' as const }] :
    []),
]);

function period(order: MedicationOrder): string {
  const from = `Desde ${order.humanStartsAt}`;

  return order.humanEndsAt ? `${from} hasta ${order.humanEndsAt}` : from;
}

function openNew() {
  replacing.value = null;
  showForm.value = true;
}

function openChange(order: MedicationOrder) {
  replacing.value = order;
  showForm.value = true;
}

const release = useMutation({
  mutationFn: (order: MedicationOrder) => medicationsApi.releaseHold(order.id),
  onSuccess: () => {
    error.value = '';
    queryClient.invalidateQueries({ queryKey: ['medicationOrders', props.patientId] });
  },
  onError: (failure) => {
    error.value = apiErrorText(failure, 'No se pudo reanudar la indicación.');
  },
});
</script>
<template>
  <div class="d-flex flex-column ga-4">
    <v-alert
      v-if="readOnly"
      type="info"
      variant="tonal"
    >
      Los medicamentos están desactivados para tu empresa: puedes consultar el historial,
      pero no registrar ni cambiar indicaciones.
    </v-alert>
    <v-alert
      v-else-if="archived"
      type="info"
      variant="tonal"
      text="Paciente archivado: sus indicaciones quedaron pausadas o suspendidas. Revisa el plan antes de reanudar."
    />
    <div class="d-flex flex-wrap align-center ga-2">
      <v-tabs
        v-model="status"
        color="primary"
      >
        <v-tab
          value="in_force"
          prepend-icon="mdi-pill"
        >
          Vigentes
        </v-tab>
        <v-tab
          value="upcoming"
          prepend-icon="mdi-calendar-clock"
        >
          Próximas
        </v-tab>
        <v-tab
          value="history"
          prepend-icon="mdi-history"
        >
          Historial
        </v-tab>
        <v-tab
          value="record"
          prepend-icon="mdi-clipboard-check-outline"
        >
          Registro
        </v-tab>
        <v-tab
          value="allergies"
          prepend-icon="mdi-allergy"
        >
          Alergias
        </v-tab>
      </v-tabs>
      <v-spacer />
      <!-- Editing waits for the allergies: an empty dialog could mark "Sin
           alergias conocidas" over ones it never loaded. -->
      <v-btn
        v-if="status === 'allergies' && canManage && !archived && allergies"
        color="primary"
        variant="flat"
        :prepend-icon="allergiesUnrecorded ? 'mdi-plus' : 'mdi-pencil'"
        @click="editingAllergies = true"
      >
        {{ allergiesUnrecorded ? 'Registrar alergias' : 'Editar alergias' }}
      </v-btn>
      <template v-else-if="status !== 'allergies' && status !== 'record' && canManage && !archived">
        <v-btn
          v-if="status === 'in_force' && orders.length > 0"
          variant="outlined"
          prepend-icon="mdi-pause"
          @click="acting = { order: orders[0], action: 'holdAll' }"
        >
          Pausar todas
        </v-btn>
        <v-btn
          color="primary"
          variant="flat"
          prepend-icon="mdi-plus"
          @click="openNew"
        >
          Nueva indicación
        </v-btn>
      </template>
    </div>
    <v-alert
      v-if="error"
      type="error"
      variant="tonal"
      :text="error"
      closable
      @click:close="error = ''"
    />

    <medication-day-record
      v-if="status === 'record'"
      :patient-id="patientId"
      :read-only="readOnly"
    />
    <medication-allergies-panel
      v-else-if="status === 'allergies'"
      :allergies="allergies"
      :loading="allergiesPending"
    />
    <v-data-table
      v-else
      v-bind="tableProps"
      :header-props="{ class: 'font-weight-bold' }"
      :headers="headers"
      :items="orders"
      :loading="isPending"
      loading-text="Cargando indicaciones..."
      :no-data-text="status === 'history' ? 'Sin indicaciones anteriores' : 'Sin indicaciones'"
      density="comfortable"
    >
      <template #[`item.drugName`]="{ item }">
        <span class="font-weight-medium">{{ item.drugName }}</span>
        <v-tooltip
          v-if="allergic(item)"
          text="Alergia"
          location="top"
        >
          <template #activator="{ props: tooltipProps }">
            <span
              v-bind="tooltipProps"
              class="ml-1"
              role="img"
              aria-label="Alergia"
            >
              <v-icon
                icon="mdi-alert"
                color="error"
                size="small"
              />
            </span>
          </template>
        </v-tooltip>
        <!-- No "Editado" mark: an order is never edited; its stop, pause or
             void already shows as its state. -->
        <clinical-record-history
          item-type="MedicationOrder"
          :item-id="item.id"
          :label="`Indicación de ${item.drugName}`"
        />
        <div class="d-flex flex-wrap ga-1 mt-1">
          <v-chip
            v-if="item.highAlert"
            color="error"
            size="x-small"
            variant="tonal"
          >
            Alto riesgo
          </v-chip>
          <v-chip
            v-if="item.onHold"
            color="warning"
            size="x-small"
            variant="tonal"
          >
            En pausa
          </v-chip>
          <v-chip
            v-if="status === 'history'"
            size="x-small"
            variant="tonal"
          >
            {{ item.humanPeriodStatus }}
          </v-chip>
        </div>
      </template>
      <template #[`item.dosages`]="{ item }">
        <div
          v-for="dosage in item.dosages"
          :key="dosage.id"
        >
          {{ dosage.summary }}
          <span
            v-if="dosage.indication"
            class="text-medium-emphasis"
          >· {{ dosage.indication }}</span>
          <span
            v-if="dosage.instructions"
            class="d-block text-caption text-medium-emphasis"
          >{{ dosage.instructions }}</span>
        </div>
      </template>
      <template #[`item.startsAt`]="{ item }">
        {{ period(item) }}
        <clinical-author-line
          :author="item.author"
          :at="item.humanCreatedAt"
        />
        <span
          v-if="item.stopReason"
          class="d-block text-caption text-medium-emphasis"
        >{{ item.stopReason }}</span>
        <clinical-author-line
          :author="item.stopper"
          label="Suspendida por"
        />
        <template v-if="item.state === 'entered_in_error'">
          <span
            v-if="item.voidReason"
            class="d-block text-caption text-medium-emphasis"
          >{{ item.voidReason }}</span>
          <clinical-author-line
            :author="item.voider"
            :at="item.humanVoidedAt"
            label="Anulada por"
          />
        </template>
        <a
          v-if="item.prescriptionDocumentUrl"
          :href="item.prescriptionDocumentUrl"
          target="_blank"
          rel="noopener"
          class="d-block text-caption"
        >Ver receta</a>
      </template>
      <template #[`item.actions`]="{ item }">
        <v-menu location="bottom end">
          <template #activator="{ props: menuProps }">
            <v-btn
              v-bind="menuProps"
              icon="mdi-dots-vertical"
              size="small"
              variant="text"
              aria-label="Más acciones"
            />
          </template>
          <v-list density="compact">
            <v-list-item
              title="Cambiar dosis u horario"
              @click="openChange(item)"
            />
            <v-list-item
              v-if="item.onHold"
              title="Reanudar"
              @click="release.mutate(item)"
            />
            <v-list-item
              v-else
              title="Pausar"
              @click="acting = { order: item, action: 'hold' }"
            />
            <v-list-item
              title="Suspender"
              @click="acting = { order: item, action: 'stop' }"
            />
            <v-list-item
              title="Anular por error"
              @click="acting = { order: item, action: 'void' }"
            />
          </v-list>
        </v-menu>
      </template>
    </v-data-table>

    <medication-order-form
      v-if="showForm"
      :key="replacing?.id ?? 'new'"
      :patient-id="patientId"
      :replacing="replacing"
      @close="showForm = false"
      @change-order="openChange"
    />
    <medication-order-action-dialog
      v-if="acting"
      :order="acting.order"
      :action="acting.action"
      @close="acting = null"
    />
    <medication-allergies-dialog
      v-if="editingAllergies"
      :patient-id="patientId"
      :allergies="allergies ?? []"
      @close="editingAllergies = false"
    />
  </div>
</template>
